import { Component, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateService } from '@ngx-translate/core';
import { Account } from 'src/app/models/Admin/Account.model';
import { AdministrativeService } from 'src/app/services/administrative/administrative.service';
import { AccreditationSefaz } from '../../wizard/nfce/accreditation-sefaz/accreditation-sefaz.page';

@Component({
  selector: 'app-account-management',
  templateUrl: './account-management.component.html',
  styleUrls: ['./account-management.component.scss']
})
export class AccountManagementComponent implements OnInit {

  @ViewChild('changeAccountTypeDialog') changeAccountTypeTemplate!: TemplateRef<any>;
  private changeAccountTypeDialog!: MatDialogRef<any>;

  @ViewChild('changeAccountActiveDialog') changeAccountActiveTemplate!: TemplateRef<any>;
  private changeAccountActiveDialog!: MatDialogRef<any>;

  @ViewChild('changeAccountEmailDialog') changeAccountEmailTemplate!: TemplateRef<any>;
  private changeAccountEmailDialog!: MatDialogRef<any>;
  public newAccountEmail: string = "";

  public changeAccountDocumentForm: FormGroup = new FormGroup({
    document: new FormControl("", [Validators.maxLength(18)])
  });

  private lastValidFilter: { id: string, email: string, document: string } | null = null;

  public filters: FormGroup = new FormGroup({
      id: new FormControl("", [Validators.maxLength(10)]),
      email: new FormControl("", [Validators.maxLength(50)]),
      document: new FormControl("", [Validators.maxLength(18)])
    },
    { validators: this.atLeastOneFieldValidator() }
  );

  readonly displayedColumns: string[] = ['column-id', 'column-email', 'column-document', 'column-account-type', 'column-account-active', 'column-options'];
  public dataSource: Account[] = [];
  public totalItems: number = 0;

  public pageSize: number = 20;
  public pageSizeOptions: number[] = [20, 50, 100];

  private accountTypesOptions: { id: number, name: string }[] = [];
  public getAccountTypesOptions(actualAccountType: number): {id:number, name:string}[] { return this.accountTypesOptions.filter(type => type.id != actualAccountType); }
  public selectedAccountType: number | null = null;

  private changingAccountType: boolean = false;
  private changingAccountActive: boolean = false;
  private changingAccountEmail: boolean = false;
  private changingAccountDocument: boolean = false;

  constructor(
    private adminService: AdministrativeService,
    private snackBar: MatSnackBar,
    private translateService: TranslateService,
    private dialog: MatDialog
  ) {
    
  }

  async ngOnInit(): Promise<void> {
    this.accountTypesOptions = await this.adminService.getAccountTypes();
  }

  clickFilter(useLastValidFilter: boolean = false) {
    if ((this.filters.invalid && (useLastValidFilter == false))
      || (useLastValidFilter == true && this.lastValidFilter == null)) {
      this.snackBar.open(this.translateService.instant("admin.account-management.errors.input-one-field"), '', {
        duration: 6000,
        verticalPosition: 'top',
        panelClass: ['red-snackbar']
      });
      return;
    }
    if (useLastValidFilter == true && this.lastValidFilter != null) {
      this.filters.controls['id'].setValue(this.lastValidFilter.id);
      this.filters.controls['email'].setValue(this.lastValidFilter.email);
      this.filters.controls['document'].setValue(this.lastValidFilter.document);
    }
    this.loadAccounts(0, this.pageSize);
  }

  clickChangeAccountType(account: Account) {
    this.changeAccountTypeDialog = this.dialog.open(
      this.changeAccountTypeTemplate,
      <MatDialogConfig>{ data: { account: account } }
    );

    this.changeAccountTypeDialog.afterClosed().subscribe(()=>{
      this.selectedAccountType = null;
    });
  }

  clickChangeAccountActive(account: Account) {
    this.changeAccountActiveDialog = this.dialog.open(
      this.changeAccountActiveTemplate,
      <MatDialogConfig>{ data: { account: account } }
    );
  }

  clickChangeAccountEmail(account: Account) {
    this.newAccountEmail = account.email;
    this.changeAccountDocumentForm.controls['document'].setValue(account.document);
    this.changeAccountEmailDialog = this.dialog.open(
      this.changeAccountEmailTemplate,
      <MatDialogConfig>{ data: { account: account } }
    );
  }

  clickCheckClientsInformations(account: Account) {
    if (!account?.id) return;
    const clientInfoUrl = `/admin/client-informations?clientId=${account.id}`; 
    window.open(clientInfoUrl, '_blank');
  }

  confirmChangeAccountType(account: Account) {
    if (this.selectedAccountType == null || this.selectedAccountType == undefined || this.changingAccountType == true)
      return;
    
    this.changingAccountType = true;
    this.adminService.putAccountType(account.id, this.selectedAccountType).then((success) => {
      this.changingAccountType = false;
      if (success == true) {
        this.snackBar.open(this.translateService.instant("admin.account-management.account-type-changed"), '', {
          duration: 6000,
          verticalPosition: 'top',
          panelClass: ['green-snackbar']
        });
        this.changeAccountTypeDialog.close();
        this.clickFilter(true);
      } else {
        this.snackBar.open(this.translateService.instant("api.internalServerError"), '', {
          duration: 6000,
          verticalPosition: 'top',
          panelClass: ['red-snackbar']
        });
        this.changeAccountTypeDialog.close();
      }
    });
  }

  confirmChangeAccountActive(account: Account, newValue: boolean) {
    if (this.changingAccountActive == true)
      return;
    
    this.changingAccountActive = true;
    this.adminService.putAccountActive(account.id, newValue).then((success) => {
      this.changingAccountActive = false;
      if (success == true) {
        let messageFlag = newValue ? "account-activated" : "account-inactivated";
        this.snackBar.open(this.translateService.instant("admin.account-management."+messageFlag), '', {
          duration: 6000,
          verticalPosition: 'top',
          panelClass: ['green-snackbar']
        });
        this.changeAccountActiveDialog.close();
        this.clickFilter(true);
      } else {
        this.snackBar.open(this.translateService.instant("api.internalServerError"), '', {
          duration: 6000,
          verticalPosition: 'top',
          panelClass: ['red-snackbar']
        });
        this.changeAccountActiveDialog.close();
      }
    });
  }

  async confirmChangeAccountEmailAndDocument(account: Account) {
    if (this.changingAccountEmail == true || this.changingAccountDocument == true)
      return;

    const document = this.changeAccountDocumentForm.controls['document'].value;
    if (!this.newAccountEmail || this.changeAccountDocumentForm.invalid || !document)
      return;

    this.changingAccountEmail = true;
    this.changingAccountDocument = true;

    try {
      const emailResult = await this.adminService.putAccountEmail(account.id, this.newAccountEmail);
      if (emailResult.success != true) {
        this.snackBar.open(emailResult.message ?? this.translateService.instant("api.internalServerError"), '', {
          duration: 6000,
          verticalPosition: 'top',
          panelClass: ['red-snackbar']
        });
        return;
      }

      const documentResult = await this.adminService.putAccountDocument(account.id, document);
      if (documentResult.success != true) {
        this.snackBar.open(documentResult.message ?? this.translateService.instant("api.internalServerError"), '', {
          duration: 6000,
          verticalPosition: 'top',
          panelClass: ['red-snackbar']
        });
        return;
      }

      this.snackBar.open(this.translateService.instant("admin.account-management.account-email-changed"), '', {
        duration: 6000,
        verticalPosition: 'top',
        panelClass: ['green-snackbar']
      });
      this.changeAccountEmailDialog.close();
      this.clickFilter(true);
    } finally {
      this.changingAccountEmail = false;
      this.changingAccountDocument = false;
    }
  }

  paginationChange(event: any) {
    this.loadAccounts(event.pageIndex, event.pageSize);
  }

  async loadAccounts(page: number, pageSize: number) {
    let ret = await this.adminService.getAccounts(page, pageSize,
      this.filters.controls['id'].value, this.filters.controls['email'].value, this.filters.controls['document'].value);

    this.lastValidFilter = {
      id: this.filters.controls['id'].value,
      email: this.filters.controls['email'].value,
      document: this.filters.controls['document'].value
    };

    if (ret == null) {
      this.lastValidFilter = null;
    }

    this.dataSource = ret?.accounts ?? [];
    this.totalItems = ret?.totalItems ?? 0;
  }

  atLeastOneFieldValidator(): ValidatorFn  {
    return (control: AbstractControl): ValidationErrors | null => {
      const group = control as FormGroup;
      const isAnyFieldFilled = Object.values(group.controls).some(
        control => control.value && control.value.trim() !== ""
      );
      return isAnyFieldFilled ? null : { atLeastOneRequired: true };
    };
  }

}
